import { FC, useState, useCallback } from 'react';
import { addDays, startOfDay } from 'date-fns';
import { AvailabilityCalendar, CalendarTimeSlot } from '../../../components/AvailabilityCalendar';
import { useBooking } from '../../../contexts/BookingContext';

const DAYS_AHEAD = 60;

export const Step3DateTimeSelection: FC = () => {
  const { formData, updateFormData, nextStep } = useBooking();

  // Local state for selected date
  const [selectedDate, setSelectedDate] = useState<Date | null>(formData.date || null);

  // Selected time from form data
  const [selectedTime, setSelectedTime] = useState<string | null>(formData.startTime || null);

  // Calculate min/max dates
  const today = startOfDay(new Date());
  const minDate = today;
  const maxDate = addDays(today, DAYS_AHEAD);

  // Handle date selection
  const handleDateSelect = useCallback(
    (date: Date) => {
      setSelectedDate(date);
      setSelectedTime(null); // Clear time when date changes
      updateFormData({
        date,
        startTime: '',
        endTime: '',
      });
    },
    [updateFormData]
  );

  // Handle time slot selection
  const handleTimeSelect = useCallback(
    (slot: CalendarTimeSlot) => {
      if (!slot.isAvailable || !selectedDate) return;

      setSelectedTime(slot.startTime);
      updateFormData({
        date: selectedDate,
        startTime: slot.startTime,
        endTime: slot.endTime,
      });

      // Auto-advance after selecting time
      setTimeout(() => {
        nextStep();
      }, 150);
    },
    [selectedDate, updateFormData, nextStep]
  );

  return (
    <div>
      <div className="mb-6">
        <h2 className="text-xl font-semibold text-gray-900 dark:text-slate-100">Pick a date and time</h2>
        <p className="mt-1 text-gray-600 dark:text-slate-400">
          Select your preferred appointment slot from the available options.
        </p>
      </div>

      <AvailabilityCalendar
        serviceId={formData.serviceId}
        therapistId={formData.therapistId}
        selectedDate={selectedDate}
        selectedTime={selectedTime}
        onDateSelect={handleDateSelect}
        onTimeSelect={handleTimeSelect}
        minDate={minDate}
        maxDate={maxDate}
      />
    </div>
  );
};
